// Live Performance page. GET /api/v1/portfolio/performance[?client_id=].
// Window slicing (1M/3M/YTD/1Y/ITD) is client-side. The synthetic "BTC
// rebased" series from the prototype was removed — benchmark series needs
// real historical prices, and benchmark_available is false until then.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ExportCsvLink, KickerRow, PageHeader, SortTable, Stat } from '../../components/shell';
import { fmtPct, fmtUSD, fmtUSDcompact } from '../../lib/format';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePolling } from '../../lib/usePolling';

// Performance only changes at period close (monthly). Slow poll is fine.
const PERFORMANCE_POLL_MS = 5 * 60_000;

interface MonthRow {
  month: string;
  opening: number;
  ending: number;
  flows: number;
  ret: number;
}

interface PerformancePayload {
  monthly: MonthRow[];
  summary: {
    itd_pct: number;
    max_drawdown_pct: number;
    peak_month: string | null;
    trough_month: string | null;
    first_month: string | null;
    last_month: string | null;
    n_months: number;
    sharpe_ratio: number | null;
    sharpe_min_months: number;
    btc_itd_pct?: number;
  };
  benchmark_available: boolean;
}

const WINDOWS = ['1M', '3M', 'YTD', '1Y', 'ITD'] as const;
type Window = typeof WINDOWS[number];

export default function Performance() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [windowSel, setWindowSel] = useState<Window>('ITD');

  useEffect(() => {
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
    }
  }, [user, clientId, navigate]);

  const url = '/portfolio/performance' + (clientId ? `?client_id=${clientId}` : '');
  const pollingEnabled = !!user && (user.role === 'client' || !!clientId);
  const { data, error } = usePolling<PerformancePayload>(
    (signal) => api<PerformancePayload>(url, { signal }),
    { intervalMs: PERFORMANCE_POLL_MS, deps: [url], enabled: pollingEnabled },
  );

  useEffect(() => {
    if (!error) { setErrorMsg(null); return; }
    const status = (error as { status?: number })?.status;
    if (status === 404 && user?.role !== 'client') { navigate('/fund', { replace: true }); return; }
    setErrorMsg(status === 409 ? 'Your portfolio is being set up. Transactions and reports will appear here once trading begins.' : 'Failed to load performance.');
  }, [error, user, navigate]);

  const monthly = data?.monthly ?? [];
  const sliced = useMemo<MonthRow[]>(() => {
    if (windowSel === 'ITD') return monthly;
    if (windowSel === '1Y') return monthly.slice(-12);
    if (windowSel === 'YTD') {
      const currentYear = monthly.length ? monthly[monthly.length - 1]!.month.split(' ').pop() : '';
      return monthly.filter(m => m.month.endsWith(currentYear || ''));
    }
    if (windowSel === '3M') return monthly.slice(-3);
    if (windowSel === '1M') return monthly.slice(-1);
    return monthly;
  }, [windowSel, monthly]);

  // Compound the per-month returns inside the chosen window.
  const periodReturn = useMemo(() => {
    if (!sliced.length) return 0;
    const factor = sliced.reduce((acc, m) => acc * (1 + m.ret / 100), 1);
    return (factor - 1) * 100;
  }, [sliced]);

  if (errorMsg) return <div className="view container"><p className="serif-body" style={{ color: 'var(--accent)' }}>{errorMsg}</p></div>;
  if (!data) return (
    <div className="view container">
      <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: 'var(--ink-3)' }}>Loading…</p>
    </div>
  );

  const { summary } = data;
  const noData = monthly.length === 0;
  const winLabel = windowSel === 'ITD' ? 'ITD' : windowSel;

  return (
    <div className="view container">
      <PageHeader
        kicker="PERFORMANCE"
        title={<><em>Returns.</em></>}
        meta={
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 0 }}>
              {WINDOWS.map(w => (
                <button key={w} className="filter-chip"
                        data-active={windowSel === w ? '1' : '0'}
                        onClick={() => setWindowSel(w)}>{w}</button>
              ))}
            </div>
            <ExportCsvLink endpoint="performance" clientId={clientId} />
          </div>
        }
      />

      {noData ? (
        <p className="serif-body" style={{ fontSize: 18, color: 'var(--ink-2)' }}>
          <em>No issued statements yet.</em> Performance series populates as monthly periods close — see <a href="/statements" style={{ color: 'var(--ink)' }}>statements</a> for the issue schedule.
        </p>
      ) : (
        <>
          <div className="agg-strip" style={{ ['--cols' as any]: 3 } as any}>
            <div className="agg-cell">
              <div className="stat-label">INCEPTION-TO-DATE</div>
              <div className="stat-num" style={{
                fontSize: 'clamp(48px, 5.5vw, 80px)',
                color: summary.itd_pct >= 0 ? 'var(--accent)' : 'var(--oxblood)',
              }}>
                {fmtPct(summary.itd_pct)}
              </div>
              <div className="stat-sub">{summary.n_months} MONTHS · SINCE {(summary.first_month || '').toUpperCase()}</div>
            </div>
            <div className="agg-cell">
              <div className="stat-label">{winLabel} RETURN</div>
              <div className="stat-num" style={{
                fontSize: 'clamp(48px, 5.5vw, 80px)',
                color: periodReturn >= 0 ? 'var(--accent)' : 'var(--oxblood)',
              }}>
                {fmtPct(periodReturn)}
              </div>
              <div className="stat-sub">
                {sliced.length ? `${sliced[0]!.month.toUpperCase()} → ${sliced[sliced.length - 1]!.month.toUpperCase()}` : '—'}
              </div>
            </div>
            <div className="agg-cell">
              <div className="stat-label">BENCHMARK · BTC ITD</div>
              <div className="stat-num" style={{
                fontSize: 'clamp(48px, 5.5vw, 80px)',
                color: data.benchmark_available
                  ? ((summary.btc_itd_pct ?? 0) >= 0 ? 'var(--accent)' : 'var(--oxblood)')
                  : 'var(--ink-3)',
              }}>
                {data.benchmark_available
                  ? fmtPct(summary.btc_itd_pct ?? 0)
                  : '—'}
              </div>
              <div className="stat-sub">
                {data.benchmark_available
                  ? `vs PORTFOLIO ${fmtPct(summary.itd_pct)}`
                  : 'BENCHMARK PENDING'}
              </div>
            </div>
          </div>

          <div className="agg-strip" style={{ ['--cols' as any]: 2, marginTop: 32 } as any}>
            <div className="agg-cell">
              <div className="stat-label">SHARPE RATIO (ANN.)</div>
              <div className="stat-num" style={{
                fontSize: 'clamp(40px, 4.5vw, 64px)',
                color: summary.sharpe_ratio == null
                  ? 'var(--ink-3)'
                  : (summary.sharpe_ratio >= 0 ? 'var(--accent)' : 'var(--oxblood)'),
              }}>
                {summary.sharpe_ratio == null ? '—' : summary.sharpe_ratio.toFixed(2)}
              </div>
              <div className="stat-sub">
                {summary.sharpe_ratio == null
                  ? `NEEDS ${summary.sharpe_min_months} MONTHS · HAVE ${summary.n_months}`
                  : `RF RATE 5.00% · ${summary.n_months} MONTHS`}
              </div>
            </div>
            <div className="agg-cell">
              <div className="stat-label">MAX DRAWDOWN</div>
              <div className="stat-num" style={{
                fontSize: 'clamp(40px, 4.5vw, 64px)',
                color: summary.max_drawdown_pct < 0 ? 'var(--oxblood)' : 'var(--ink-3)',
              }}>
                {summary.max_drawdown_pct === 0
                  ? '0.00%'
                  : `${summary.max_drawdown_pct.toFixed(2)}%`}
              </div>
              <div className="stat-sub">
                {summary.peak_month && summary.trough_month
                  ? `${summary.peak_month.toUpperCase()} → ${summary.trough_month.toUpperCase()}`
                  : 'NO DRAWDOWN'}
              </div>
            </div>
          </div>

          {sliced.length >= 2 && (
            <section style={{ marginTop: 72 }}>
              <KickerRow kicker={`VALUE OVER TIME · ${winLabel}`} />
              <PerformanceChart data={sliced.map(m => ({ x: m.month, y: m.ending }))} />
              <div style={{ display: 'flex', gap: 32, marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 16, height: 2, background: 'var(--accent)' }} />
                  <span className="label-s">PORTFOLIO</span>
                </div>
              </div>
            </section>
          )}

          <section className="clean-panel perf-months" style={{ marginTop: 96 }}>
            <KickerRow kicker="RETURNS BY MONTH" />
            <SortTable
              rows={monthly}
              defaultSort="month"
              defaultDir="desc"
              columns={[
                { key: 'month', label: 'MONTH', sortBy: (r: any) => monthly.indexOf(r) },
                {
                  key: 'opening', label: 'OPENING AUM', align: 'right',
                  render: (r: any) => <span className="num">{fmtUSD(r.opening, 0)}</span>,
                },
                {
                  key: 'flows', label: 'NET FLOWS', align: 'right',
                  render: (r: any) => (
                    <span className="num" style={{ color: r.flows ? 'var(--ink-2)' : 'var(--ink-3)' }}>
                      {r.flows ? (r.flows > 0 ? '+' : '') + fmtUSD(r.flows, 0) : '—'}
                    </span>
                  ),
                },
                {
                  key: 'ending', label: 'ENDING AUM', align: 'right',
                  render: (r: any) => <span className="num">{fmtUSD(r.ending, 0)}</span>,
                },
                {
                  key: 'ret', label: 'PERIOD RETURN', align: 'right',
                  render: (r: any) => <span className={'num ' + (r.ret >= 0 ? 'pos' : 'neg')}>{fmtPct(r.ret)}</span>,
                },
              ]}
            />
          </section>

          <section className="clean-hide" style={{ marginTop: 96 }}>
            <KickerRow kicker="DRAWDOWN · INCEPTION-TO-DATE" />
            <div className="dd-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 56, alignItems: 'start' }}>
              <DrawdownChart monthly={monthly} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <Stat
                  label="MAX DRAWDOWN"
                  value={
                    <span className={summary.max_drawdown_pct < 0 ? 'neg' : ''} style={{ fontSize: 56 }}>
                      {summary.max_drawdown_pct === 0 ? '0.00%' : `${summary.max_drawdown_pct.toFixed(2)}%`}
                    </span>
                  }
                />
                {summary.peak_month && summary.trough_month && (
                  <div>
                    <div className="label" style={{ marginBottom: 6 }}>PEAK → TROUGH</div>
                    <div className="num" style={{ fontSize: 14 }}>
                      {summary.peak_month.toUpperCase()} → {summary.trough_month.toUpperCase()}
                    </div>
                  </div>
                )}
                <hr className="rule" />
                <p className="serif-body" style={{ margin: 0, fontSize: 15 }}>
                  Deepest peak-to-trough decline measured month-over-month against the trailing ending AUM.
                </p>
              </div>
            </div>
          </section>
        </>
      )}

      <section className="clean-hide" style={{
        marginTop: 96, padding: '32px 0',
        borderTop: 'var(--hairline)', borderBottom: 'var(--hairline)',
        display: 'grid', gridTemplateColumns: '200px 1fr', gap: 32,
      }}>
        <div className="label">METHODOLOGY</div>
        <div className="serif-body" style={{ fontSize: 16 }}>
          <p style={{ margin: '0 0 12px' }}>
            Period returns are time-weighted (Modified Dietz), weighting each cash flow by the fraction of the period it was invested. Issued statements additionally present net-of-fee returns. Inception-to-date compounds the per-period returns; drawdown is measured on ending AUM. The BTC benchmark is unmanaged, bears no fees, and is shown for reference only — not a target.
          </p>
          <p style={{ margin: 0, color: 'var(--ink-3)' }}>
            <em style={{ color: 'var(--ink-3)' }}>
              Figures are unaudited. Past performance does not guarantee future results. Digital assets are not FDIC- or SIPC-insured and may lose value, including total loss of principal.
            </em>
          </p>
        </div>
      </section>

      <style>{`
        @media (max-width: 1000px) {
          .dd-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ─── Single-series area + line chart ──────────────────────────────────────
function PerformanceChart({ data }: { data: { x: string; y: number }[] }) {
  const W = 1480, H = 360;
  const padL = 0, padR = 80, padT = 24, padB = 36;

  const ys = data.map(d => d.y);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const yPad = (yMax - yMin) * 0.08;
  const yLo = yMin - yPad, yHi = yMax + yPad;
  const yRange = yHi - yLo || 1;

  const N = data.length;
  const xAt = (i: number) => padL + (N === 1 ? (W - padL - padR) / 2 : (i / (N - 1)) * (W - padL - padR));
  const yAt = (y: number) => padT + (1 - (y - yLo) / yRange) * (H - padT - padB);

  const [hover, setHover] = useState<number | null>(null);
  const onMove = (e: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xRel = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0, bd = Infinity;
    for (let i = 0; i < N; i++) {
      const d = Math.abs(xAt(i) - xRel);
      if (d < bd) { bd = d; best = i; }
    }
    setHover(best);
  };

  const ticks = 4;
  const path = data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(2)} ${yAt(p.y).toFixed(2)}`).join(' ');

  return (
    <div style={{ position: 'relative', marginTop: 16 }} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height: H, display: 'block' }}
           onMouseMove={onMove}>
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const t = i / ticks;
          const y = padT + t * (H - padT - padB);
          const v = yHi - t * yRange;
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--rule-soft)" strokeWidth="0.5" />
              <text x={W - padR + 10} y={y + 4} fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)" letterSpacing="0.05em">
                {fmtUSDcompact(v)}
              </text>
            </g>
          );
        })}
        {[0, Math.floor(N / 3), Math.floor(2 * N / 3), N - 1].filter(i => N > 1 || i === 0).map(i => (
          <text key={i} x={xAt(i)} y={H - 10}
                fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)" letterSpacing="0.05em"
                textAnchor={i === 0 ? 'start' : i === N - 1 ? 'end' : 'middle'}>
            {data[i]!.x.toUpperCase()}
          </text>
        ))}
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
        {hover != null && (
          <>
            <line x1={xAt(hover)} x2={xAt(hover)} y1={padT} y2={H - padB}
                  stroke="var(--ink)" strokeWidth="0.5" strokeDasharray="2 3" />
            <circle cx={xAt(hover)} cy={yAt(data[hover]!.y)}
                    r="3.5" fill="var(--paper)" stroke="var(--accent)" strokeWidth="1.5" />
          </>
        )}
      </svg>
      {hover != null && (
        <div style={{
          position: 'absolute', top: 8, left: `${(xAt(hover) / W) * 100}%`,
          transform: 'translateX(-50%)',
          background: 'var(--paper)', border: '1px solid var(--ink)',
          padding: '10px 14px', whiteSpace: 'nowrap', pointerEvents: 'none',
        }}>
          <div className="label-s" style={{ marginBottom: 6, color: 'var(--ink-3)' }}>
            {data[hover]!.x.toUpperCase()}
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 16,
            fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)',
          }}>
            <span>PORTFOLIO</span>
            <span className="num">{fmtUSD(data[hover]!.y, 0)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Drawdown area chart ──────────────────────────────────────────────────
function DrawdownChart({ monthly }: { monthly: MonthRow[] }) {
  const W = 800, H = 220;
  const padL = 0, padR = 60, padT = 16, padB = 28;

  const series = useMemo(() => {
    let peak = -Infinity;
    return monthly.map(m => {
      if (m.ending > peak) peak = m.ending;
      const dd = peak > 0 ? ((m.ending - peak) / peak) * 100 : 0;
      return { x: m.month, y: dd };
    });
  }, [monthly]);

  if (series.length < 2) {
    return <p className="serif-body" style={{ color: 'var(--ink-3)' }}>Not enough history for a drawdown chart yet.</p>;
  }

  const yMin = Math.min(...series.map(p => p.y), 0);
  const yMax = 0;
  const yRange = (yMax - yMin) || 1;
  const xAt = (i: number) => padL + (i / (series.length - 1)) * (W - padL - padR);
  const yAt = (y: number) => padT + (1 - (y - yMin) / yRange) * (H - padT - padB);

  const areaPath = [
    `M ${xAt(0)} ${yAt(0)}`,
    ...series.map((p, i) => `L ${xAt(i).toFixed(2)} ${yAt(p.y).toFixed(2)}`),
    `L ${xAt(series.length - 1)} ${yAt(0)} Z`,
  ].join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H }}>
      <path d={areaPath} fill="var(--oxblood-tint)" stroke="var(--oxblood)" strokeWidth="1" />
      <line x1={padL} x2={W - padR} y1={yAt(0)} y2={yAt(0)} stroke="var(--ink)" strokeWidth="0.5" />
      {[0, 0.5, 1].map(t => {
        const y = yMin + t * yRange;
        return (
          <text key={t} x={W - padR + 8} y={yAt(y) + 4}
                fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)" letterSpacing="0.05em">
            {y.toFixed(1)}%
          </text>
        );
      })}
      {[0, Math.floor(series.length / 2), series.length - 1].map(i => (
        <text key={i} x={xAt(i)} y={H - 8}
              fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)" letterSpacing="0.05em"
              textAnchor={i === 0 ? 'start' : i === series.length - 1 ? 'end' : 'middle'}>
          {series[i]!.x.toUpperCase()}
        </text>
      ))}
    </svg>
  );
}
